import { useEffect, useRef, useState } from "react";
import {
  Button,
  Grid,
  TextInput,
  Textarea,
  Group,
  NumberInput,
  ActionIcon,
  Loader,
  Center,
  Radio,
  Text,
  Image,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createNewRecord,
  getAllData,
  updateRecord,
} from "../../services/common.service";
import { displayNotification } from "../../commonComponents/notifications/displayNotification";
import { COLORS } from "../../constants/colors";
import DigitalSignature from "../../commonComponents/components/digitalSignature/DigitalSignature";
import TimeInputComp from "../../commonComponents/components/dateInput/TimeInputComp";
import DateInputComp from "../../commonComponents/components/dateInput/DateInputComp";
import dayjs from "dayjs";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import SelectWithCleared from "../../commonComponents/components/selectWithAPI/SelectWithCleared";
import CrewSelect from "./CrewSelect";
import ModalComp from "../../commonComponents/components/modal/ModalComp";
import useAuthStore from "../../store/authStore";

const InflightSheet = ({ recordId, onClose, mode }) => {
  const queryClient = useQueryClient();

  const { auth } = useAuthStore()
  const role = auth?.emp_role_id?.role_name?.toLowerCase();

  const empCode = auth?.emp_code;

  const [sigVisibleCrew, setSigVisibleCrew] = useState(false);
  const [sigVisibleSCC, setSigVisibleSCC] = useState(false);
  const [showAdditionalSeniorCrew, setShowAdditionalSeniorCrew] =
    useState(false);
  const [showAdditionalCrew, setShowAdditionalCrew] = useState(false);

  const [viewCrewSignatureModal, setViewCrewSignatureModal] = useState(false);
  const [viewSCCSignatureModal, setViewSccSignatureModal] = useState(false);

  const sigCanvasCrew = useRef(null);
  const sigCanvasSCC = useRef(null);

  const form = useForm({
    initialValues: {
      id: "",
      journey_id: "",
      journey_leg_id: "",
      base: "GOX",
      flight_date: dayjs().format("YYYY-MM-DD"),
      c1_name: "",
      c1_staff_id: null,
      c2_name: "",
      c2_staff_id: null,
      c3_name: "",
      c3_staff_id: null,
      c4_name: "",
      c4_staff_id: null,
      flight_no: "",
      sector: "",
      ac_reg_no: "",
      p1_name: "",
      p1_staff_id: null,
      p2_name: "",
      p2_staff_id: null,
      p3_name: "",
      p3_staff_id: null,
      total_adults: 0,
      total_infants: 0,
      total_pax: 0,
      special_pax: 0,
      total_sales: 0,
      d_30: null,
      d_60: null,
      d_90: null,
      d_120: null,
      handover_from: "",
      transit: "",
      handover_to: "",
      company_mail_remarks: "",
      delay: false,
      cabin_apperance: false,
      cdlb_entry: false,
      passenger_issue: false,
      catering_issue: false,
      disruptive_pax: false,
      safety_incidents: false,
      issue_description: "",
      pilot_signature: "",
      scc_signature: "",
    },
    validate: {
      journey_leg_id: (value) => (value ? null : "Please select an Journey"),
      journey_id: (value) => (value ? null : "Please select an Journey"),
      d_30: (value) => (value ? null : "D-30 is required"),
      scc_signature: (value) =>
        value && value.length > 0 ? null : "SCC signature is required",
    },
  });

  // ✅ Universal handleChange
  const handleChange = (field, value) => {
    form.setFieldValue(field, value);
  };

  // Cockpit selected IDs
  const cockpitIds = [
    form.values.p1_staff_id,
    form.values.p2_staff_id,
    form.values.p3_staff_id,
  ].filter(Boolean);

  // Cabin selected IDs
  const cabinIds = [
    form.values.c1_staff_id,
    form.values.c2_staff_id,
    form.values.c3_staff_id,
    form.values.c4_staff_id,
  ].filter(Boolean);

  // Fetch existing record
  const { data: existingData, isLoading } = useQuery({
    queryKey: ["inflight-report", recordId],
    queryFn: () =>
      getAllData({ source: "inflight_report", filter: { id_eq: recordId } }),
    enabled: !!recordId,
  });

  // Fetch journey details
  const { data: journeyDetails } = useQuery({
    queryKey: ["journey-leg-details", form.values.journey_leg_id],
    queryFn: () =>
      getAllData({
        source: "journey_leg",
        filter: { id_eq: form.values.journey_leg_id },
      }),
    enabled: !!form.values.journey_leg_id,
  });

  // Populate journey data automatically
  useEffect(() => {
    if (existingData?.data?.length > 0) {
      const d = existingData.data[0];
      Object.entries({
        id: d.id || "",
        journey_id: d.journey_id?.id || "",
        journey_leg_id: d.journey_leg_id?.id || "",
        base: d.base || "GOX",
        flight_date: d.flight_date || dayjs().format("YYYY-MM-DD"),
        c1_name: d.c1_name || "",
        c1_staff_id: d.c1_staff_id || null,
        c2_name: d.c2_name || "",
        c2_staff_id: d.c2_staff_id || null,
        c3_name: d.c3_name || "",
        c3_staff_id: d.c3_staff_id || null,
        c4_name: d.c4_name || "",
        c4_staff_id: d.c4_staff_id || null,
        flight_no: d.flight_no || "",
        sector: d.sector || "",
        ac_reg_no: d.ac_reg_no || "",
        p1_name: d.p1_name || "",
        p1_staff_id: d.p1_staff_id || null,
        p2_name: d.p2_name || "",
        p2_staff_id: d.p2_staff_id || null,
        p3_name: d.p3_name || "",
        p3_staff_id: d.p3_staff_id || null,
        total_adults: d.total_adults || 0,
        total_infants: d.total_infants || 0,
        total_pax: d.total_pax || 0,
        special_pax: d.special_pax || 0,
        total_sales: d.total_sales || 0,
        d_30: d.d_30 || null,
        d_60: d.d_60 || null,
        d_90: d.d_90 || null,
        d_120: d.d_120 || null,
        handover_from: d.handover_from || "",
        transit: d.transit || "",
        handover_to: d.handover_to || "",
        company_mail_remarks: d.company_mail_remarks || "",
        delay: d.is_delay || false,
        cabin_apperance: d.cabin_apperance || false,
        cdlb_entry: d.cdlb_entry || false,
        passenger_issue: d.pax_issue || false,
        catering_issue: d.catering_issue || false,
        disruptive_pax: d.disruptive_pax || false,
        safety_incidents: d.safety_incident || false,
        issue_description: d.issue_description || "",
        pilot_signature: d.pilot_signature || "",
        scc_signature: d.scc_signature || "",
      }).forEach(([key, value]) => handleChange(key, value));
    } else if (journeyDetails?.data?.length > 0) {
      const leg = journeyDetails?.data?.[0] || journeyDetails?.data?.data?.[0];
      handleChange("flight_no", leg.flight_number || "");
      handleChange(
        "sector",
        leg.depart_airport_id?.airport_code &&
          leg.arrive_airport_id?.airport_code
          ? `${leg.depart_airport_id.airport_code} - ${leg.arrive_airport_id.airport_code}`
          : ""
      );
      handleChange("ac_reg_no", leg.aircraft_id?.tail_reg_no || "");
      handleChange("p1_name", leg.p1_name);
      handleChange("p1_staff_id", leg.p1_staff_id || null);
      handleChange("p2_name", leg.p2_name || "");
      handleChange("p2_staff_id", leg.p2_staff_id || null);
      handleChange("p3_name", leg.p3_name || "");
      handleChange("p3_staff_id", leg.p3_staff_id || null);
      handleChange("c1_name", leg.c1_name || "");
      handleChange("c1_staff_id", leg.c1_staff_id || null);
      handleChange("c2_name", leg.c2_name || "");
      handleChange("c2_staff_id", leg.c2_staff_id || null);
      handleChange("c3_name", leg.c3_name || "");
      handleChange("c3_staff_id", leg.c3_staff_id || null);
      handleChange("c4_name", leg.c4_name || "");
      handleChange("c4_staff_id", leg.c4_staff_id || null);
    }
  }, [journeyDetails, existingData]);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: (body) =>
      updateRecord({ body, source: "inflight_report", id: recordId }),
    onSuccess: () => {
      displayNotification({
        message: "Inflight Report updated successfully",
        variant: "success",
      });
      queryClient.invalidateQueries(["all-records", "inflight_report"]);
      onClose?.();
    },
    onError: (err) =>
      displayNotification({
        message: err?.message || "Error updating record",
        variant: "error",
      }),
  });

  const createMutation = useMutation({
    mutationFn: (body) => createNewRecord({ body, source: "inflight_report" }),
    onSuccess: () => {
      displayNotification({
        message: "Inflight Report created successfully",
        variant: "success",
      });
      queryClient.invalidateQueries(["all-records", "inflight_report"]);
      onClose?.();
    },
    onError: (err) =>
      displayNotification({
        message: err?.message || "Error creating record",
        variant: "error",
      }),
  });

  // const removeEmptyFields = (obj) => {
  //   const cleaned = {};
  //   Object.entries(obj).forEach(([key, value]) => {
  //     if (
  //       value !== null &&
  //       value !== undefined &&
  //       value !== "" &&
  //       !(typeof value === "object" && Object.keys(value).length === 0)
  //     ) {
  //       cleaned[key] = value;
  //     }
  //   });
  //   return cleaned;
  // };

  // Handle submit
  const handleSubmit = form.onSubmit((values) => {
    const payload = {
      ...values,
      is_delay: values.delay,
      cabin_apperance: values.cabin_apperance,
      cdlb_entry: values.cdlb_entry,
      pax_issue: values.passenger_issue,
      catering_issue: values.catering_issue,
      disruptive_pax: values.disruptive_pax,
      safety_incident: values.safety_incidents,
    };

    // remove UI keys not needed by backend
    delete payload.delay;
    delete payload.passenger_issue;
    delete payload.safety_incidents;

    recordId ? updateMutation.mutate(payload) : createMutation.mutate(payload);
  });

  if (isLoading)
    return (
      <Center>
        <Loader />
      </Center>
    );

  const isViewMode = mode === "view";
  // const isEditMode = mode === "edit";

  return (
    <form onSubmit={handleSubmit}>
      <Grid>
        {!recordId && (
          <Grid.Col span={12}>
            <SelectWithCleared
              label="Journey"
              placeholder="Select Journey"
              isRequired={true}
              readOnly={isViewMode}
              value={
                form.values.journey_leg_id && form.values.journey_id
                  ? `${form.values.journey_leg_id} - ${form.values.journey_id}`
                  : null
              }
              setValue={(selected) => {
                if (selected) {
                  const [journey_leg_id, journey_id] = selected
                    .split(" - ")
                    .map(Number);
                  handleChange("journey_leg_id", journey_leg_id);
                  handleChange("journey_id", journey_id);
                } else {
                  form.reset();
                }
              }}
              tableName="journey_leg"
              error={form.errors.journey_leg_id || form.errors.journey_id}
              itemData={{
                filters: JSON.stringify({
                  journey_date_eq: form.values.flight_date,

                  or:
                    role === "scc" || role === "cc"
                      ? [
                        {
                          "c1_staff_id_eq": empCode,
                          "c2_staff_id_eq": empCode,
                        }
                      ]
                      : [],

                }),
                display_options: `["flight_number","depart_airport_id.airport_code","arrive_airport_id.airport_code"]`,
                return_value: ["id", "journey_id.id"],
              }}
            />
          </Grid.Col>
        )}

        <Grid.Col span={6}>
          <TextInput
            label="Base"
            readOnly={isViewMode}
            {...form.getInputProps("base")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <DateInputComp
            label="Date"
            readOnly={isViewMode}
            value={dayjs(form.values.flight_date).toDate()}
            onChange={(val) =>
              handleChange(
                "flight_date",
                val ? dayjs(val).format("YYYY-MM-DD") : ""
              )
            }
          />
        </Grid.Col>

        <Grid.Col
          span={6}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <h3>Senior Cabin Crew</h3>
          <ActionIcon
            size="xs"
            variant="outline"
            onClick={() => setShowAdditionalSeniorCrew((prev) => !prev)}
          >
            {showAdditionalSeniorCrew ? (
              <IconMinus size={16} />
            ) : (
              <IconPlus size={16} />
            )}
          </ActionIcon>
        </Grid.Col>

        <Grid.Col
          span={6}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <h3>Cabin Crew</h3>
          <ActionIcon
            size="xs"
            variant="outline"
            onClick={() => setShowAdditionalCrew((prev) => !prev)}
          >
            {showAdditionalCrew ? (
              <IconMinus size={16} />
            ) : (
              <IconPlus size={16} />
            )}
          </ActionIcon>
        </Grid.Col>

        <Grid.Col span={3}>
          <CrewSelect
            label="C1"
            crewFieldName="c1"
            form={form}
            readOnly={isViewMode}
            allSelectedStaffIds={cabinIds}
            category="Cabin"
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            disabled
            label="Emp ID"
            value={form.values.c1_staff_id || ""}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <CrewSelect
            label="C2"
            crewFieldName="c2"
            readOnly={isViewMode}
            form={form}
            allSelectedStaffIds={cabinIds}
            category="Cabin"
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            disabled
            label="Emp ID"
            value={form.values.c2_staff_id || ""}
          />
        </Grid.Col>

        {showAdditionalSeniorCrew && (
          <>
            <Grid.Col span={6}>
              <h3>Additional Senior Cabin Crew</h3>
            </Grid.Col>
            {!showAdditionalCrew && <Grid.Col span={6}></Grid.Col>}
          </>
        )}

        {showAdditionalCrew && (
          <>
            {!showAdditionalSeniorCrew && <Grid.Col span={6}></Grid.Col>}
            <Grid.Col span={6}>
              <h3>Additional Cabin Crew</h3>
            </Grid.Col>
          </>
        )}

        {showAdditionalSeniorCrew && (
          <>
            <Grid.Col span={3}>
              <CrewSelect
                label="C3"
                crewFieldName="c3"
                form={form}
                readOnly={isViewMode}
                allSelectedStaffIds={cabinIds}
                category="Cabin"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                disabled
                label="Emp ID"
                value={form.values.c3_staff_id || ""}
              />
            </Grid.Col>
          </>
        )}

        {showAdditionalCrew && (
          <>
            {!showAdditionalSeniorCrew && <Grid.Col span={6}></Grid.Col>}

            <Grid.Col span={3}>
              <CrewSelect
                label="C4"
                crewFieldName="c4"
                form={form}
                readOnly={isViewMode}
                allSelectedStaffIds={cabinIds}
                category="Cabin"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                disabled
                label="Emp ID"
                value={form.values.c4_staff_id || ""}
              />
            </Grid.Col>
          </>
        )}

        {/* Flight & Cockpit Crew */}
        <Grid.Col span={12}>
          <h3>Flight & Cockpit Crew Details</h3>
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            readOnly={isViewMode}
            label="Flight No."
            {...form.getInputProps("flight_no")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            readOnly={isViewMode}
            label="Sector"
            {...form.getInputProps("sector")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <SelectWithCleared
            label="A/C Regn"
            readOnly={isViewMode}
            value={form.values.ac_reg_no || ""}
            setValue={(val) => handleChange("ac_reg_no", val)}
            placeholder="Select A/C Regn"
            tableName="aircrafts"
            itemData={{
              filters: JSON.stringify({ is_active_eq: true, page: -1 }),
              display_options: `["tail_reg_no"]`,
              return_value: "tail_reg_no",
            }}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <CrewSelect
            label="Captain"
            crewFieldName="p1"
            form={form}
            readOnly={isViewMode}
            allSelectedStaffIds={cockpitIds}
            isCockpit
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <CrewSelect
            label="First Officer"
            crewFieldName="p2"
            form={form}
            readOnly={isViewMode}
            allSelectedStaffIds={cockpitIds}
            isCockpit
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <CrewSelect
            label="Observer"
            crewFieldName="p3"
            form={form}
            readOnly={isViewMode}
            allSelectedStaffIds={cockpitIds}
            isCockpit
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <NumberInput
            label="Adults"
            readOnly={isViewMode}
            min={0}
            {...form.getInputProps("total_adults")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Infants"
            readOnly={isViewMode}
            min={0}
            {...form.getInputProps("total_infants")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Total PAX"
            readOnly={isViewMode}
            min={0}
            {...form.getInputProps("total_pax")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            label="Special PAX"
            readOnly={isViewMode}
            min={0}
            {...form.getInputProps("special_pax")}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <NumberInput
            label="Total Sales"
            readOnly={isViewMode}
            min={0}
            {...form.getInputProps("total_sales")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <h3>Cockpit Monitoring</h3>
        </Grid.Col>
        <Grid.Col span={3}>
          <TimeInputComp
            readOnly={isViewMode}
            label="D-30"
            {...form.getInputProps("d_30")}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TimeInputComp
            readOnly={isViewMode}
            label="D-60"
            {...form.getInputProps("d_60")}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TimeInputComp
            readOnly={isViewMode}
            label="D-90"
            {...form.getInputProps("d_90")}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TimeInputComp
            readOnly={isViewMode}
            label="D-120"
            {...form.getInputProps("d_120")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <h3>Signature</h3>
          <Button
            mt="xs"
            variant="outline"
            w={"100%"}
            onClick={() => {
              if (isViewMode) {
                if (form.values.pilot_signature) {
                  setViewCrewSignatureModal(true);
                }
              } else {
                setSigVisibleCrew(true);
              }
            }}
          >
            {isViewMode
              ? !form.values.pilot_signature
                ? "No Signature"
                : "View Signature"
              : form.values.pilot_signature && !recordId
                ? "Signature Added"
                : form.values.pilot_signature && recordId
                  ? "Update Signature"
                  : "Add Signature"}
          </Button>
        </Grid.Col>
        <Grid.Col span={12}>
          <h3>Company Mail</h3>
        </Grid.Col>

        <Grid.Col span={4}>
          <TextInput
            label="Handover from"
            placeholder="Enter Handover From"
            readOnly={isViewMode}
            value={form.values.handover_from || ""}
            onChange={(e) => handleChange("handover_from", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <TextInput
            label="Transit"
            placeholder="Enter Transit"
            readOnly={isViewMode}
            value={form.values.transit || ""}
            onChange={(e) => handleChange("transit", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <TextInput
            label="Handover to"
            placeholder="Enter Handover To"
            readOnly={isViewMode}
            value={form.values.handover_to || ""}
            onChange={(e) => handleChange("handover_to", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Textarea
            label="Details of Items"
            readOnly={isViewMode}
            placeholder="Enter the details of items"
            value={form.values.company_mail_remarks || ""}
            onChange={(e) =>
              handleChange("company_mail_remarks", e.target.value)
            }
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <h3>Issues to Report</h3>
        </Grid.Col>

        {[
          "delay",
          "cabin_apperance",
          "cdlb_entry",
          "passenger_issue",
          "catering_issue",
          "disruptive_pax",
          "safety_incidents",
        ].map((field) => {
          // Override label just for UI display
          const label =  field.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());

          return (
            <Grid.Col span={4} key={field}>
              <Radio.Group
                label={label}
                readOnly={isViewMode}
                value={form.values[field] ? "true" : "false"}
                onChange={(val) => handleChange(field, val === "true")}
              >
                <Group mt="xs">
                  <Radio value="true" label="Yes" />
                  <Radio value="false" label="No" />
                </Group>
              </Radio.Group>
            </Grid.Col>
          );
        })}


        <Grid.Col span={12}>
          <Textarea
            label="Issue Description"
            placeholder="Enter issue description"
            value={form.values.issue_description || ""}
            readOnly={isViewMode}
            onChange={(e) => handleChange("issue_description", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <h3>Signature of SCC</h3>
          <Button
            mt="xs"
            variant="outline"
            w={"100%"}
            onClick={() => {
              if (isViewMode) {
                if (form.values.scc_signature) {
                  setViewSccSignatureModal(true);
                }
              } else {
                setSigVisibleSCC(true);
              }
            }}
          >
            {isViewMode
              ? !form.values.scc_signature
                ? "No Signature"
                : "View Signature"
              : form.values.scc_signature && !recordId
                ? "Signature Added"
                : form.values.scc_signature && recordId
                  ? "Update Signature"
                  : "Add Signature"}
          </Button>
        </Grid.Col>

        <Group position="right" mt="md">
          <Button
            variant="light"
            color="gray"
            onClick={() => onClose()}
            type="button"
          >
            Cancel
          </Button>

          {(role === "scc" || role === "cc") && !recordId && (
            <Button
              color={COLORS.green}
              type="submit"
              loading={createMutation.isPending}
            >
              Save
            </Button>
          )}

          {(role !== "scc" && role !== "cc") && (
            <Button
              color={COLORS.green}
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {recordId ? "Update" : "Save"}
            </Button>
          )}

        </Group>

        <ModalComp
          opened={viewCrewSignatureModal}
          onClose={() => setViewCrewSignatureModal(false)}
          title="Crew Signature"
          size="md"
          centered
        >
          {form.values.pilot_signature ? (
            <Box ta="center">
              <Image
                src={form.values.pilot_signature}
                alt="Crew Signature"
                fit="contain"
                radius="md"
                height={300}
                withPlaceholder
              />
            </Box>
          ) : (
            <Text ta="center" c="dimmed">
              No signature available
            </Text>
          )}
        </ModalComp>
        <ModalComp
          opened={viewSCCSignatureModal}
          onClose={() => setViewSccSignatureModal(false)}
          title="SCC Signature"
          size="md"
          centered
        >
          {form.values.scc_signature ? (
            <Box ta="center">
              <Image
                src={form.values.scc_signature}
                alt="SCC Signature"
                fit="contain"
                radius="md"
                height={300}
                withPlaceholder
              />
            </Box>
          ) : (
            <Text ta="center" c="dimmed">
              No signature available
            </Text>
          )}
        </ModalComp>

        {/* Digital Signatures */}
        <DigitalSignature
          sigCanvas={sigCanvasCrew}
          visible={sigVisibleCrew}
          onClose={() => setSigVisibleCrew(false)}
          onSave={(dataURL) => {
            handleChange("pilot_signature", dataURL);
            setSigVisibleCrew(false);
          }}
        />
        <DigitalSignature
          sigCanvas={sigCanvasSCC}
          visible={sigVisibleSCC}
          onClose={() => setSigVisibleSCC(false)}
          onSave={(dataURL) => {
            handleChange("scc_signature", dataURL);
            setSigVisibleSCC(false);
          }}
        />
      </Grid>
    </form>
  );
};

export default InflightSheet;
